import React, { useState, useCallback, useId } from 'react';
import { LinkSimple, X } from '@phosphor-icons/react';
import api from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';
import { useCollectStatus } from '../hooks/useCollectStatus';
import Modal from './Modal';

const PANEL_MOTION = {
  initial: { opacity: 0, scale: 0.95, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

const URL_PATTERN = /^https?:\/\//i;

/**
 * Save-URL entry point (COL-137): POST /collect a URL, then either resolve
 * immediately (already_collected) or poll the CollectorJob to completion.
 *
 * Deliberately does NOT resolve the created item/ProcessingJob itself — once
 * the collector job completes it only has an item_id (CollectorJob has no
 * ProcessingJob job_id of its own, a different id space entirely). It hands
 * that item_id up via onCollected/onAlreadyCollected and lets the caller
 * (Gallery) do the item + ProcessingJob lookup and cache-patch, exactly like
 * handleUploadComplete does for image uploads — same must-show-a-real-error
 * bar, same single source of truth for the processingJobs/cache state.
 */
export default function SaveUrlModal({ onClose, onCollected, onAlreadyCollected }) {
  const titleId = useId();
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState(null);

  const { targetRef: modalRef, unlock: unlockScroll } = useScrollLock(true);

  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [onClose, unlockScroll]);

  const handleCollectComplete = useCallback((jobData) => {
    onCollected(jobData.item_id);
  }, [onCollected]);

  const handleCollectFailed = useCallback((jobData) => {
    setJobId(null);
    setSubmitting(false);
    setError(jobData.error || 'Failed to save this URL.');
  }, []);

  const { isTimedOut } = useCollectStatus(jobId, {
    enabled: !!jobId,
    onComplete: handleCollectComplete,
    onError: handleCollectFailed,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = url.trim();
    if (!trimmed) return;
    if (!URL_PATTERN.test(trimmed)) {
      setError('Enter a valid URL starting with http:// or https://');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await api.collectUrl(trimmed);
      if (response.status === 'already_collected') {
        onAlreadyCollected(response.item_id);
        return;
      }
      // 202 queued — start polling. submitting stays true until the poll
      // resolves (complete/failed) or times out.
      setJobId(response.job_id);
    } catch (err) {
      setSubmitting(false);
      setError(err.message || 'Failed to save this URL.');
    }
  };

  const isCollecting = submitting && !!jobId;

  return (
    <Modal
      onClose={handleClose}
      labelledBy={titleId}
      className="save-url-modal"
      panelRef={modalRef}
      panelMotionProps={PANEL_MOTION}
      panelStyle={{
        background: 'var(--surface-raised)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-2xl)',
        maxWidth: '480px',
        width: '90%',
        boxShadow: 'var(--shadow-xl)',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--space-xl)',
      }}>
        <h2 id={titleId} style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
        }}>
          Save from URL
        </h2>
        <button
          onClick={handleClose}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            cursor: 'pointer',
          }}
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
          padding: 'var(--space-md)',
          background: 'var(--surface-sunken)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
        }}>
          <LinkSimple size={20} color="var(--text-muted)" />
          <input
            // type="text", not "url" — HTML5's native url constraint
            // validation would block form submission (and our own onSubmit
            // handler) before URL_PATTERN's http(s)-only check ever runs,
            // for most malformed input. Validation is entirely our own so
            // the message shown always matches what actually failed.
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.instagram.com/p/..."
            disabled={submitting}
            autoFocus
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text-primary)',
              fontSize: '0.9375rem',
            }}
          />
        </div>

        <p style={{
          fontSize: '0.8125rem',
          color: 'var(--text-muted)',
          marginTop: 'var(--space-sm)',
        }}>
          Paste a link to an Instagram post, TikTok, YouTube video, article, or recipe.
        </p>

        {isCollecting && (
          <p style={{
            fontSize: '0.8125rem',
            color: 'var(--text-secondary)',
            marginTop: 'var(--space-md)',
          }}>
            {isTimedOut
              ? 'Still working on it — this is taking longer than usual. You can close this and check back shortly.'
              : 'Fetching page details...'}
          </p>
        )}

        {error && (
          <div style={{
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            padding: 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--space-md)',
            fontSize: '0.875rem',
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-md)',
          marginTop: 'var(--space-xl)',
        }}>
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting && !isTimedOut}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!url.trim() || submitting}
          >
            {isCollecting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
